/**
 * usePeopleDirectory — a lightweight React binding that loads the team rosters
 * and exposes the deduped people directory for the app's people pickers.
 *
 * Strict layering: calls {@link TeamStore} through the facade only. Failure is
 * non-fatal — pickers simply fall back to free-text entry with no suggestions.
 */

import { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { TeamStore } from './teamStore';
import { buildPeopleDirectory } from './teamModel';
import type { DirectoryPerson } from './types';

export interface UsePeopleDirectoryResult {
  people: DirectoryPerson[];
  loading: boolean;
}

export function usePeopleDirectory(): UsePeopleDirectoryResult {
  const api = useApi();
  const store = useMemo(() => new TeamStore(api), [api]);
  const [people, setPeople] = useState<DirectoryPerson[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    store
      .listSummaries()
      .then((teams) => {
        if (!cancelled) setPeople(buildPeopleDirectory(teams));
      })
      .catch(() => {
        /* non-fatal: pickers fall back to free-text with no suggestions */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [store]);

  return { people, loading };
}
